/**
 * Pure geometry for the routine-timer radial arc. Splits a routine's steps into
 * proportional wedges around a ring (clockwise from the top), tracking how far
 * each wedge has elapsed. The RoutineArc component turns these into SVG strokes;
 * keeping the math here makes it testable and shared across arc styles.
 */

export interface RoutineArcSegment {
  /** Step index this segment represents. */
  index: number;
  /** Fraction [0,1) around the ring where the segment starts (clockwise from top). */
  start: number;
  /** Length of the segment as a fraction of the ring. */
  sweep: number;
  /** How much of THIS segment has elapsed [0,1] (1 = done, 0 = not started). */
  fill: number;
  /** Domain accent colour for the segment. */
  color: string;
}

/**
 * Build the wedge segments for the whole-routine arc.
 *
 * @param durations  per-step planned minutes (length N). Non-positive values are
 *                   floored to a tiny slice so every step is still visible.
 * @param colors     per-step accent colour (length N).
 * @param currentIndex  the step currently running.
 * @param currentFraction  elapsed fraction [0,1] of the current step.
 */
export function buildRoutineArcSegments(
  durations: number[],
  colors: string[],
  currentIndex: number,
  currentFraction: number,
): RoutineArcSegment[] {
  const n = durations.length;
  if (n === 0) return [];

  // Floor each weight so zero/negative-duration steps (rare) still get a sliver.
  const weights = durations.map((d) => (Number.isFinite(d) && d > 0 ? d : 0.01));
  const total = weights.reduce((a, b) => a + b, 0) || 1;

  const cf = Math.min(1, Math.max(0, currentFraction));
  const segments: RoutineArcSegment[] = [];
  let acc = 0;
  for (let i = 0; i < n; i++) {
    const sweep = weights[i] / total;
    const fill = i < currentIndex ? 1 : i === currentIndex ? cf : 0;
    segments.push({ index: i, start: acc, sweep, fill, color: colors[i] ?? '#888' });
    acc += sweep;
  }
  return segments;
}

/**
 * Overall routine progress [0,1] — completed steps plus the current step's
 * elapsed fraction, weighted by planned duration. Drives the linear progress bar.
 */
export function routineOverallProgress(
  durations: number[],
  currentIndex: number,
  currentFraction: number,
): number {
  const segments = buildRoutineArcSegments(durations, [], currentIndex, currentFraction);
  return segments.reduce((sum, s) => sum + s.sweep * s.fill, 0);
}
